/**
 * HubAgentsPage — HUB-AGENT-PERSONAS-V1.
 *
 * Persona-first /agents index. Each agent_status row renders as a
 * persona card (avatar + display_name + role_title + status dot +
 * one-line current focus) instead of the technical row format that
 * shipped in Phase 8a. Click → /agents/<agent_id> per-persona page.
 *
 * Toggle between persona-card view (default) and dense-table view
 * (debugging). Group by department under collapsible headers.
 *
 * Backed by GET /hub/agents (Phase 1 schema + 00496 persona columns).
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Pill, StatusDot } from './components'
import { formatCurrency } from '@/utils/formatCurrency'
import {
  hubAutonomy, type HubAutonomyAgent,
} from './api'
import { personaInitials, personaTone, deptLabel, DEPT_ORDER } from './personas'

function statusVariant(s: HubAutonomyAgent): 'live' | 'info' | 'err' | 'warn' | 'ghost' {
  if (s.paused) return 'warn'
  if (s.current_status === 'errored') return 'err'
  if (s.current_status === 'running') return 'live'
  if (s.current_status === 'idle') return 'info'
  return 'ghost'
}

function relTime(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function HubAgentsPage() {
  const [agents, setAgents] = useState<HubAutonomyAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<'cards' | 'table'>('cards')
  const [filterProduct, setFilterProduct] = useState<string>('')

  const refresh = async () => {
    try {
      const res = await hubAutonomy.agents({ include_paused: true })
      setAgents(res.agents)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [])

  const filtered = useMemo(() => {
    return agents.filter(a => {
      if (filterProduct && a.product !== filterProduct) return false
      return true
    })
  }, [agents, filterProduct])

  const byDept = useMemo(() => {
    const map: Record<string, HubAutonomyAgent[]> = {}
    for (const a of filtered) {
      const d = a.department || 'other'
      if (!map[d]) map[d] = []
      map[d].push(a)
    }
    for (const d of Object.keys(map)) {
      map[d].sort((a, b) => (a.display_name || a.agent_id).localeCompare(b.display_name || b.agent_id))
    }
    return map
  }, [filtered])

  const productCounts = useMemo(() => {
    const by: Record<string, number> = {}
    for (const a of agents) by[a.product] = (by[a.product] ?? 0) + 1
    return by
  }, [agents])

  const orderedDepts = useMemo(
    () => Object.keys(byDept).sort((a, b) => {
      const ai = DEPT_ORDER.indexOf(a)
      const bi = DEPT_ORDER.indexOf(b)
      const av = ai === -1 ? DEPT_ORDER.length : ai
      const bv = bi === -1 ? DEPT_ORDER.length : bi
      return av - bv
    }),
    [byDept],
  )

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
        <h1 style={{ margin: 0 }}>Agents</h1>
        <span style={{ color: 'var(--text-dim)' }}>{filtered.length} {view === 'cards' ? 'personas' : 'rows'}</span>
        <span className="grow" />
        <select value={filterProduct} onChange={e => setFilterProduct(e.target.value)} style={selectStyle}>
          <option value="">all products</option>
          {Object.keys(productCounts).map(p => (
            <option key={p} value={p}>{p} ({productCounts[p]})</option>
          ))}
        </select>
        <div style={toggleStyle}>
          <button
            type="button"
            onClick={() => setView('cards')}
            style={{ ...toggleBtn, ...(view === 'cards' ? toggleBtnActive : {}) }}
          >Cards</button>
          <button
            type="button"
            onClick={() => setView('table')}
            style={{ ...toggleBtn, ...(view === 'table' ? toggleBtnActive : {}) }}
          >Table</button>
        </div>
      </div>

      {error && (
        <Card>
          <div style={{ padding: 12, color: 'var(--err, #f88)' }}>{error}</div>
        </Card>
      )}

      {loading && (
        <Card>
          <div style={{ padding: 14, color: 'var(--text-dim)' }}>Loading agents…</div>
        </Card>
      )}

      {!loading && view === 'cards' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {orderedDepts.length === 0 && (
            <Card><div style={{ padding: 14, color: 'var(--text-dim)' }}>No agents match the current filter.</div></Card>
          )}
          {orderedDepts.map(dept => (
            <section key={dept} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8,
                fontSize: 11, color: 'var(--text-dim)', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                <span>{deptLabel(dept)}</span>
                <span style={{ color: 'var(--text-dim, #666)' }}>· {byDept[dept].length}</span>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 12,
              }}>
                {byDept[dept].map(a => <PersonaCard key={a.agent_id} agent={a} />)}
              </div>
            </section>
          ))}
        </div>
      )}

      {!loading && view === 'table' && (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
                  <th style={th}>Agent</th>
                  <th style={th}>Product</th>
                  <th style={th}>Dept</th>
                  <th style={th}>Status</th>
                  <th style={th}>Last action</th>
                  <th style={th}>Sessions today</th>
                  <th style={th}>24h actions</th>
                  <th style={th}>Budget</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.agent_id} style={{ borderBottom: '1px solid var(--border, rgba(255,255,255,0.04))' }}>
                    <td style={{ ...td, fontFamily: 'monospace' }}>
                      <StatusDot tone={a.paused ? 'warn' : a.current_status === 'errored' ? 'err' : a.current_status === 'running' ? 'ok' : 'info'} />
                      <Link
                        to={`/agents/${encodeURIComponent(a.agent_id)}`}
                        style={{ marginLeft: 6, color: 'var(--text)' }}
                      >{a.agent_id}</Link>
                    </td>
                    <td style={td}>
                      <Pill variant={a.product === 'tenon' ? 'info' : a.product === 'benchpoint' ? 'live' : 'ghost'}>{a.product}</Pill>
                    </td>
                    <td style={td}>{deptLabel(a.department)}</td>
                    <td style={td}>
                      <Pill variant={statusVariant(a)}>{a.paused ? 'paused' : a.current_status}</Pill>
                    </td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>{relTime(a.last_action_at)}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{a.sessions_today}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{a.action_count_24h}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{formatCurrency(a.budget_usd_spent, { decimals: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function PersonaCard({ agent }: { agent: HubAutonomyAgent }) {
  const display = agent.display_name || agent.agent_id
  const initials = personaInitials(display)
  const tone = personaTone(display)
  const subtitle = agent.role_title || agent.agent_id
  const focus = agent.paused
    ? 'Paused'
    : agent.session_count_active > 0
      ? `${agent.session_count_active} active session${agent.session_count_active === 1 ? '' : 's'}`
      : agent.last_action_at
        ? `Last active ${relTime(agent.last_action_at)}`
        : 'Idle'
  return (
    <Link
      to={`/agents/${encodeURIComponent(agent.agent_id)}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div style={{
        background: 'var(--surface, rgba(255,255,255,0.03))',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
        borderRadius: 10,
        padding: 14,
        display: 'flex', flexDirection: 'column', gap: 10,
        height: '100%',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: tone, color: '#0c0c0e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, letterSpacing: '0.02em',
            flexShrink: 0,
          }}>{initials}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
          </div>
          <StatusDot tone={agent.paused ? 'warn' : agent.current_status === 'errored' ? 'err' : agent.current_status === 'running' ? 'ok' : 'info'} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
          {focus}
        </div>
        <div style={{ display: 'flex', gap: 6, fontSize: 11, color: 'var(--text-dim)', fontFamily: 'monospace', flexWrap: 'wrap' }}>
          <span>{agent.sessions_today} today</span>
          <span>·</span>
          <span>{agent.action_count_24h} actions/24h</span>
          <span>·</span>
          <span>{formatCurrency(agent.budget_usd_spent, { decimals: 2 })}</span>
        </div>
      </div>
    </Link>
  )
}

const selectStyle: React.CSSProperties = {
  padding: '4px 8px',
  background: 'var(--surface-2, rgba(255,255,255,0.05))',
  color: 'var(--text)',
  border: '1px solid var(--border, rgba(255,255,255,0.1))',
  borderRadius: 6,
  fontSize: 12,
}

const toggleStyle: React.CSSProperties = {
  display: 'flex',
  border: '1px solid var(--border, rgba(255,255,255,0.1))',
  borderRadius: 6,
  overflow: 'hidden',
}

const toggleBtn: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  background: 'transparent',
  color: 'var(--text)',
  border: 'none',
  cursor: 'pointer',
}

const toggleBtnActive: React.CSSProperties = {
  background: 'var(--surface-2, rgba(255,255,255,0.08))',
}

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }
const td: React.CSSProperties = { padding: '10px 10px', verticalAlign: 'middle' }
